import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import logo from "../../assets/images/Support_logo.png";
import phoneIcon from "../../assets/images/Support_call.png";
import SupportCallBg from "../../assets/images/Support_call_bg.png";
import SubHeader from "../../components/Header/SubmenuHeader";

const Support = () => {
  const navigate = useNavigate();

  const [formData, setFormData] = useState({
    about: "",
    subject: "",
    customMessage: "",
  });

  const [errors, setErrors] = useState({});
  const [selectedIssue, setSelectedIssue] = useState("");
  const [result, setResult] = useState("");

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleRadioChange = (e) => {
    const { name, value } = e.target;
    if (name === "about") {
      setSelectedIssue(""); // Reset quick issue when category changes
    }
    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const validateForm = () => {
    const newErrors = {};

    const hasCategory = !!formData.about;
    const hasQuickIssue = !!selectedIssue;
    const hasMessage = !!formData.customMessage.trim();
    const hasSubject = !!formData.subject.trim();

    // 1️⃣ Message validation
    if (!hasQuickIssue && !hasMessage) {
      newErrors.message = "Please select a quick issue or type your message.";
    }

    // 2️⃣ Subject validation (STRICTLY per your table)
    if (
      (!hasCategory && !hasQuickIssue && !hasMessage && !hasSubject) || // nothing selected
      (hasMessage && !hasSubject) // message typed only
    ) {
      newErrors.subject = "Subject is required.";
    }

    // 3️⃣ Category validation (only when nothing selected)
    if (!hasCategory && !hasQuickIssue && !hasMessage) {
      newErrors.about = "Please select where the issue occurred.";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!validateForm()) return;

    setResult("Sending...");

    // Build a clean message: quick issue + typed message (if both exist)
    let finalMessage = "";
    if (selectedIssue) {
      finalMessage += `Selected Issue: ${selectedIssue}\n\n`;
    }
    if (formData.customMessage.trim()) {
      finalMessage += formData.customMessage.trim();
    }
    if (!finalMessage) finalMessage = "(No additional details provided)";

    const form = new FormData();
    form.append("access_key", "9c78354d-81f1-46e9-a853-291123304c44");
    form.append(
      "subject",
      formData.subject.trim() || "Support Request - Locker Issue",
    );
    form.append("about", formData.about);
    form.append("message", finalMessage); // This is what appears as "Message" in email

    // Optional: Add extra fields for better email readability
    form.append("category", formData.about); // Shows clearly as "Category"

    try {
      const response = await fetch("https://api.web3forms.com/submit", {
        method: "POST",
        body: form,
      });

      const data = await response.json();

      if (data.success) {
        setResult(
          "Thank you! Your support request has been sent successfully.",
        );
        // Reset everything
        setFormData({ about: "", subject: "", customMessage: "" });
        setSelectedIssue("");
        setErrors({});
      } else {
        setResult(data.message || "Failed to send. Please try again.");
      }
    } catch (err) {
      setResult("Network error. Please check your internet.");
    }
  };

  const WebAppQuestions = [
    "I'm not receiving parcel notifications or OTPs.",
    "My parcel shows as pending even though it has been delivered.",
    "How do I approve, reject, or manage a delivery?",
    "How do I add or manage members linked to my account?",
  ];

  const LockerQuestions = [
    "The locker didn't open after I entered the OTP.",
    "The QR code is not scanning on the locker.",
    "The locker screen is frozen or not responding.",
    "Can someone else collect the parcel for me?",
  ];

  const otherQuestions = [
    "My parcel is missing. What should I do?",
    "The parcel in the locker doesn't belong to me.",
    "How long can a parcel remain in the locker?",
    "How do I update my contact details?",
  ];

  const getIssueQuestions = () => {
    if (formData.about === "Web App") return WebAppQuestions;
    if (formData.about === "Locker System Screen") return LockerQuestions;
    if (formData.about === "other") return otherQuestions;
    return [];
  };

  return (
    <div className="w-full flex flex-col items-center bg-[#FDFEFF] min-h-screen">
      <SubHeader title="Support" />

      <div
        className="mt-12 w-full max-w-[409px] h-[89.37px] flex justify-between items-center px-4 bg-cover bg-center rounded-full"
        style={{ backgroundImage: `url(${SupportCallBg})` }}
      >
        <img src={logo} alt="Logo" className="w-[60px] h-[60px]" />
        <div className="flex flex-col justify-center items-start text-white leading-tight">
          <span className="text-lg font-semibold">+91 9892233193</span>
          <span className="text-sm font-normal">Customer Support</span>
        </div>
        <a href="tel:+919892233193">
          <img src={phoneIcon} alt="Call" className="w-[91.19px] h-[54.09px]" />
        </a>
      </div>

      <form
        onSubmit={handleSubmit}
        className="mt-10 w-full max-w-[620px] p-4 md:p-6 flex flex-col gap-6"
      >
        <div className="w-full flex justify-center items-center">
          <h1 className="font-dongle font-normal text-[42px] sm:text-[54px] md:text-[62px] leading-[46px] text-center">
            Have Questions? Let’s Connect!
          </h1>
        </div>

        <div className="border border-gray-300 rounded p-4">
          <label className="block font-semibold text-[18px] mb-3">
            Where are you facing the issue?
          </label>
          <div className="flex flex-wrap gap-4">
            {["Web App", "Locker System Screen", "other"].map((option) => (
              <label
                key={option}
                className="flex items-center gap-2 capitalize"
              >
                <input
                  type="radio"
                  name="about"
                  value={option}
                  checked={formData.about === option}
                  onChange={handleRadioChange}
                />
                {option === "other" ? "Other" : option}
              </label>
            ))}
          </div>
          {errors.about && (
            <p className="text-red-500 text-sm mt-1">{errors.about}</p>
          )}

          {formData.about && getIssueQuestions().length > 0 && (
            <div className="mt-5">
              <p className="font-medium mb-2">Quick issues in this category:</p>
              <div className="flex flex-col gap-3">
                {getIssueQuestions().map((q, index) => (
                  <label
                    key={index}
                    className="flex items-center gap-2 border border-gray-200 rounded px-3 py-2 hover:bg-gray-50 cursor-pointer"
                  >
                    <input
                      type="radio"
                      name="quickIssue"
                      value={q}
                      checked={selectedIssue === q}
                      onChange={(e) => setSelectedIssue(e.target.value)}
                    />
                    <span className="text-[16px]">{q}</span>
                  </label>
                ))}
              </div>
            </div>
          )}
        </div>

        <div className="border border-gray-300 rounded p-4 flex flex-col gap-4">
          <div>
            <input
              type="text"
              name="subject"
              placeholder="Subject (e.g., QR Code Not Scanning)"
              value={formData.subject}
              onChange={handleChange}
              className="w-full border border-gray-300 px-4 py-2 rounded-md text-[16px]"
            />
            {errors.subject && (
              <p className="text-red-500 text-sm mt-1">{errors.subject}</p>
            )}
          </div>

          <div>
            <textarea
              name="customMessage"
              placeholder="Tell us more about your issue... (optional if you selected a quick issue)"
              value={formData.customMessage}
              onChange={handleChange}
              className="w-full h-[110px] border border-gray-300 px-4 py-3 rounded-md resize-none text-[16px]"
            />
            {errors.message && (
              <p className="text-red-500 text-sm mt-1">{errors.message}</p>
            )}
          </div>
        </div>

        <button
          type="submit"
          className="w-full py-3 rounded-full bg-[#2699FF] text-white text-lg font-semibold transition hover:bg-[#1a88e5]"
        >
          Send Message
        </button>

        {result && (
          <p
            className={`text-center mt-4 font-semibold ${
              result.includes("success") ? "text-green-600" : "text-red-600"
            }`}
          >
            {result}
          </p>
        )}
      </form>
    </div>
  );
};

export default Support;
